import { WorkExperienceData } from '../WorkExperience';
import { appTranslate } from '../../../i18n';

export const getVisibleExperiences = (
    experiences: WorkExperienceData[],
    showAll: boolean,
    maxVisibleItems: number
): WorkExperienceData[] => {
    if (showAll || experiences.length <= maxVisibleItems) {
        return experiences;
    }
    return experiences.slice(0, maxVisibleItems);
};

export const groupExperiencesByCompany = (experiences: WorkExperienceData[]): WorkExperienceData[][] => {
    if (experiences.length === 0) return [];
    return experiences.reduce<WorkExperienceData[][]>((acc, exp) => {
        const currentCompany = exp.company?.name?.toLowerCase();
        const lastGroup = acc[acc.length - 1];
        const lastCompany = lastGroup?.[0]?.company?.name?.toLowerCase();

        if (!lastGroup || currentCompany !== lastCompany) {
            return [...acc, [exp]];
        } else {
            return [...acc.slice(0, -1), [...lastGroup, exp]];
        }
    }, []);
};

export const calculateTotalDuration = (experiences: WorkExperienceData[]): string => {
    let totalMonths = 0;

    experiences.forEach((exp) => {
        if (exp.startDate) {
            const startDate = new Date(exp.startDate * 1000);
            const endDate = exp.endDate ? new Date(exp.endDate * 1000) : new Date();

            const yearsDiff = endDate.getFullYear() - startDate.getFullYear();
            const monthsDiff = endDate.getMonth() - startDate.getMonth();

            totalMonths += yearsDiff * 12 + monthsDiff;
        }
    });

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (years === 0) {
        return months === 1 ? '1 mo' : `${months} mos`;
    } else if (months === 0) {
        return years === 1 ? '1 yr' : `${years} yrs`;
    } else {
        const yearText = years === 1 ? '1 yr' : `${years} yrs`;
        const monthText = months === 1 ? '1 mo' : `${months} mos`;
        return `${yearText} ${monthText}`;
    }
};

export const formatDateRange = (startDate?: number | null, endDate?: number | null): string => {
    if (!startDate) return '';

    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    };

    const start = formatDate(startDate);
    const end = endDate ? formatDate(endDate) : 'Present';
    return `${start} - ${end}`;
};

const employmentTypeTranslationKeys: Record<string, string> = {
    FULL_TIME: 'WorkExperience.employment_type.full_time',
    PART_TIME: 'WorkExperience.employment_type.part_time',
    CONTRACT: 'WorkExperience.employment_type.contract',
    FREELANCE: 'WorkExperience.employment_type.freelance',
    INTERNSHIP: 'WorkExperience.employment_type.internship',
    SELF_EMPLOYED: 'WorkExperience.employment_type.self_employed',
};

export const getEmploymentTypeCopy = (employmentType?: string): string => {
    if (!employmentType || employmentType === 'UNSPECIFIED') return '';
    return appTranslate(
        (employmentTypeTranslationKeys[employmentType] as any) || 'WorkExperience.employment_type.other'
    );
};
